'use client';
import { useRef, useState } from 'react';
import { ICON_ALLOWLIST, type MemoryCategoryIcon } from '../../lib/categoryIconNames';
import { resolveIcon } from '../../lib/categoryIcons';
import { useTranslation } from '../../lib/i18n';
import { Button } from './Button';

/** Shared identity-icon grid. Arrow keys move focus; Enter/Space commit the focused choice. */
export function IconPicker({ value, defaultIcon, label, onChange, disabled = false, autoFocus = false }: {
  value: string; defaultIcon: MemoryCategoryIcon; label: string;
  onChange: (icon: MemoryCategoryIcon) => void; disabled?: boolean; autoFocus?: boolean;
}) {
  const { t } = useTranslation();
  const selected = ICON_ALLOWLIST.indexOf((value || defaultIcon) as MemoryCategoryIcon);
  const [focus, setFocus] = useState(selected);
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  return (
    <div role="group" aria-label={label} className="grid grid-cols-5 gap-1"
      onKeyDown={event => {
        const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 5, ArrowUp: -5 }[event.key];
        if (step === undefined && event.key !== 'Home' && event.key !== 'End') return;
        event.preventDefault();
        event.stopPropagation();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? ICON_ALLOWLIST.length - 1
          : (focus + step! + ICON_ALLOWLIST.length) % ICON_ALLOWLIST.length;
        setFocus(next);
        buttons.current[next]?.focus();
      }}>
      {ICON_ALLOWLIST.map((name, index) => {
        const Icon = resolveIcon(name);
        const label = t.identityIcons[name];
        return <Button key={name} ref={node => { buttons.current[index] = node; }} type="button"
          variant="ghost" size="icon" disabled={disabled} tabIndex={focus === index ? 0 : -1}
          autoFocus={autoFocus && selected === index} onFocus={() => setFocus(index)}
          aria-label={label} title={label} aria-pressed={selected === index}
          className="data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary"
          data-selected={selected === index} onClick={() => onChange(name)}>
          <Icon size={16} strokeWidth={1.75} aria-hidden />
        </Button>;
      })}
    </div>
  );
}
